import { el } from '@/ui/dom';
import { sfxUiTick } from '@/audio/sfx';

/**
 * lockCta.ts — the ONE "this is part of the game" banner (owner round,
 * 2026-10-02): a resolved non-owner who taps WORLD TOUR CUP, PLAY ONLINE or
 * THE SHOP lands on that page as normal, and the page itself carries a
 * padlock + a gold BUY THE GAME call to action. The home menu buttons stay
 * clean (owner: no lock pills on the menu).
 *
 * Copy (best practice for a paid game with a demo): a plain verb + the price
 * on the button ("BUY THE GAME · $9.99"), the benefit in the line beside it.
 * The free Quick Race is a DEMO of a paid game, so nothing here says "full
 * game" (which reads as if the free part were a game on its own).
 */

/** The purchase verb, everywhere the game sells itself. One constant so a
 *  wording change (or an A/B test) is one line. */
export const BUY_GAME_LABEL = 'BUY THE GAME';

export const LOCK_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1Zm2 0h6V8a3 3 0 0 0-6 0v2Z"/></svg>';

/** The buy pill's crown + the chevron (shared: title pill, promo panel). */
export const CROWN_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M3 7.5 7.6 11 12 4.5 16.4 11 21 7.5l-1.8 10H4.8L3 7.5Z"/>' +
  '<path d="M4.8 19h14.4v1.8H4.8z"/></svg>';
export const CHEV_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';

export interface LockCtaOptions {
  /** The benefit line, e.g. "RACE ALL 9 CITIES FOR THE CUP". */
  line: string;
  /** Formatted price, e.g. "$9.99". */
  price: string;
  onBuy: () => void;
  /** M138 (owner): a SIGNED-OUT player may already own the game — a small
   *  "ALREADY OWN IT? LOG IN" link sits under the banner. Present only when
   *  the player is signed out (Flow decides); a signed-in non-owner gets the
   *  banner alone. Never on iOS: the app is paid up front, so no banner ever
   *  renders there (the gate is never armed natively). */
  onLogIn?: () => void;
  /** Extra class for per-screen placement (on the wrapper). */
  className?: string;
}

/** Build the banner (+ the optional log-in link under it) into `parent` and
 *  return the wrapper. */
export function lockCta(parent: HTMLElement, opts: LockCtaOptions): HTMLDivElement {
  const wrap = el('div', `lock-cta-wrap${opts.className ? ` ${opts.className}` : ''}`, parent);
  const box = el('div', 'lock-cta', wrap);
  el('span', 'lock-cta-ic', box).innerHTML = LOCK_SVG;
  const text = el('span', 'lock-cta-text', box);
  el('span', 'lock-cta-kicker', text).textContent = 'INCLUDED WITH DRIBBLE RACER';
  el('span', 'lock-cta-line', text).textContent = opts.line;
  const b = el('button', 'lock-cta-btn', box);
  b.type = 'button';
  b.textContent = `${BUY_GAME_LABEL} · ${opts.price} ›`;
  b.addEventListener('click', () => {
    sfxUiTick();
    opts.onBuy();
  });
  if (opts.onLogIn) {
    const onLogIn = opts.onLogIn;
    const link = el('button', 'lock-cta-login', wrap);
    link.type = 'button';
    el('span', 'lock-cta-login-q', link).textContent = 'ALREADY OWN IT?';
    el('span', 'lock-cta-login-a', link).textContent = 'LOG IN ›';
    link.addEventListener('click', () => {
      sfxUiTick();
      onLogIn();
    });
  }
  return wrap;
}
